import type { Task } from "~skillBuilder/utils/types";

import { image, link, tip, title } from "../../contentBlocks";

export const PExpectations = {
  name: "School Expectations",
  uniqueName: "p-expectations",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "professionalism.expectations/overview",
          label: "",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "School Expectations", durationTimeText: "10 min" })}
<p>Schools count on substitutes who are reliable, professionally dressed, effective communicators, and respectful of confidentiality. Upholding these expectations helps build a strong reputation, fosters trust with staff and administrators, and creates a positive learning environment for students.</p>
<div align="center">
<div style="max-width:250px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202463/skillbuilderAssets/optimized/school_expectations.jpg", alt: "Man teaching in front of class." })}
</div>
</div>
            `,
          },
        },
      ],
    },
    // Step 2
    {
      nextButtonText: "Next",
      questions: [
        // Question 2.1
        {
          attribute: "professionalism.expectations/reliability",
          label: "Reliability",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Being punctual and prepared demonstrates respect for the school, staff, and students. Review the lesson plan, familiarize yourself with classroom rules, and have a backup activity ready in case extra time needs to be filled.

${tip({ text: "Schools specify required support times in the request. Accept assignments only if you can fully commit to the scheduled time." })}

🔗 Need a backup lesson plan? [Subplan.ai](https://subplan.ai) provides free, AI-generated lesson plans to keep you prepared for anything the classroom throws your way!
            `,
          },
        },
      ],
    },
    // Step 3
    {
      nextButtonText: "Next",
      questions: [
        // Question 3.1
        {
          attribute: "professionalism.expectations/attire",
          label: "Professional attire",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Dressing in business casual attire helps set a professional tone and makes a positive impression. Choose clean, comfortable, and appropriate clothing that allows you to move easily around the classroom. Always review the request details carefully, as some schools may specify particular dress requirements.

${tip({ text: "Hygiene Matters - Schools recommend avoiding strong odors (e.g., cigarettes, perfumes/colognes) and maintaining good personal hygiene before your request." })}

<div style="margin-top: 16px;">
${link({ text: "How to dress for your request", url: "https://support.swingeducation.com/en/articles/11120182-how-should-i-dress-for-my-assignment" })}
</div>
            `,
          },
        },
      ],
    },
    // Step 4
    {
      nextButtonText: "Next",
      questions: [
        // Question 4.1
        {
          attribute: "professionalism.expectations/communication",
          label: "Professional communication",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Whether you're engaging with students, collaborating with staff, or checking in with administrators, clear and respectful communication is key.

**Do:**
<ul style="margin-left: 16px;">
<li>✅ Speak clearly and respectfully</li>
<li>✅ Keep all interactions positive and solution-focused</li>
<li>✅ Use active listening to understand student and staff needs</li>
</ul>

**Don't:**
<ul style="margin-left: 16px;">
<li>❌ Name-calling or yelling</li>
<li>❌ Conversations that could be misconstrued or inappropriate</li>
<li>❌ Straying from the lesson plan or age-appropriate topics</li>
</ul>
            `,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 5.1
        {
          attribute: "professionalism.expectations/confidentiality",
          label: "Confidentiality",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
As a substitute, you may become aware of student information, behavior, personal challenges, or school operations. This information must be kept private and professional at all times.

⚠️ **If a situation requires reporting, follow all school protocols** to ensure student safety and compliance with school policies.
            `,
          },
        },
      ],
    },
  ],
} satisfies Task;
